import { SettingsTypeEnumSchema, SoftwareEnumSchema, SoftwareSettingsMetadata } from "@domain/schemas/BI";

// Settings are scoped per search engine, so the cache key must include it or a
// SpectroMine job would reuse the Spectronaut list fetched earlier in the session.
type SettingsCacheKey = string;

const buildCacheKey = (type: SettingsTypeEnumSchema, software?: SoftwareEnumSchema): SettingsCacheKey => `${software ?? "any"}::${type}`;

const settingsListCache = new Map<SettingsCacheKey, SoftwareSettingsMetadata[]>();
const settingsListInFlight = new Map<SettingsCacheKey, Promise<SoftwareSettingsMetadata[]>>();

export async function fetchSettingsListCached(
  fetcher: (type: SettingsTypeEnumSchema, software?: SoftwareEnumSchema) => Promise<SoftwareSettingsMetadata[]>,
  type: SettingsTypeEnumSchema,
  software?: SoftwareEnumSchema,
): Promise<SoftwareSettingsMetadata[]> {
  const cacheKey = buildCacheKey(type, software);
  const cached = settingsListCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const existingRequest = settingsListInFlight.get(cacheKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    try {
      const list = (await fetcher(type, software)) ?? [];
      settingsListCache.set(cacheKey, list);
      return list;
    } finally {
      settingsListInFlight.delete(cacheKey);
    }
  })();

  settingsListInFlight.set(cacheKey, request);
  return request;
}

export function setCachedSettingsList(type: SettingsTypeEnumSchema, list: SoftwareSettingsMetadata[], software?: SoftwareEnumSchema): void {
  settingsListCache.set(buildCacheKey(type, software), list);
}

/**
Drops every cached list. Used when the cache would otherwise outlive its context.
*/
export function clearSettingsListCache(): void {
  settingsListCache.clear();
  settingsListInFlight.clear();
}
